import {
  estimateWorkspaceViewSize,
  WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN,
  WORKSPACE_VIEW_MAX_BYTES,
  WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS,
  WORKSPACE_VIEW_MAX_NAVIGATION_VISITS,
  type WorkspaceNavigationDescriptor,
} from '@streamlain/copilot-realtime-contract';

import type { CommandNavigationSearchBudget } from '../../quicksearch/impls/commands';

/** Raw admission precedes JSON/UTF8 work; this never changes action bindings. */
export class WorkspaceNavigationContextBudget implements CommandNavigationSearchBudget {
  private metadataVisits = 0;
  private textUnits = 0;
  private bytes = 2;
  private count = 0;
  private readonly limit = Math.min(
    WORKSPACE_VIEW_MAX_BYTES,
    WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS *
      WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN
  );
  truncated = false;

  withinBinding(value: string): boolean {
    return value.length <= this.limit;
  }
  visit(reference?: string): boolean {
    if (
      this.metadataVisits >= WORKSPACE_VIEW_MAX_NAVIGATION_VISITS ||
      (reference !== undefined && !this.withinBinding(reference))
    ) {
      this.truncated = true;
      return false;
    }
    this.metadataVisits++;
    return true;
  }
  text(value: string): string {
    let end = Math.min(value.length, this.limit - this.textUnits);
    if (end < value.length) {
      this.truncated = true;
      const last = value.charCodeAt(end - 1);
      if (last >= 0xd800 && last <= 0xdbff) end--;
    }
    this.textUnits += end;
    return value.slice(0, end);
  }
  admit(
    value: WorkspaceNavigationDescriptor
  ): 'accepted' | 'oversized' | 'invalid' {
    let visits = 0,
      units = 0;
    const active = new WeakSet<object>();
    const check = (entry: unknown): 'oversized' | 'invalid' | null => {
      if (++visits > WORKSPACE_VIEW_MAX_NAVIGATION_VISITS) return 'oversized';
      if (typeof entry === 'string') {
        units += entry.length;
        return units > this.limit ? 'oversized' : null;
      }
      if (entry === null || entry === undefined || typeof entry === 'boolean')
        return null;
      if (typeof entry === 'number')
        return Number.isFinite(entry) ? null : 'invalid';
      if (typeof entry !== 'object' || active.has(entry)) return 'invalid';
      const array = Array.isArray(entry);
      const prototype: unknown = Object.getPrototypeOf(entry);
      if (!array && prototype !== Object.prototype && prototype !== null)
        return 'invalid';
      active.add(entry);
      try {
        if (array) {
          if (entry.length > WORKSPACE_VIEW_MAX_NAVIGATION_VISITS)
            return 'oversized';
          for (const item of entry) {
            const issue = check(item);
            if (issue) return issue;
          }
        } else {
          for (const key in entry) {
            if (!Object.hasOwn(entry, key)) continue;
            units += key.length;
            if (units > this.limit) return 'oversized';
            const issue = check(Reflect.get(entry, key));
            if (issue) return issue;
          }
        }
        return null;
      } finally {
        active.delete(entry);
      }
    };
    const issue = check(value);
    if (issue) return issue;
    const size = estimateWorkspaceViewSize(value);
    if (!size) return 'invalid';
    const bytes = size.bytes + (this.count ? 1 : 0);
    if (this.bytes + bytes > this.limit) return 'oversized';
    this.bytes += bytes;
    this.count++;
    return 'accepted';
  }
}
